import "@/App.css";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api, errMsg, homeFor } from "@/lib/api";
import Layout from "@/components/Layout";
import { Spinner } from "@/components/bits";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminTenants from "@/pages/admin/AdminTenants";
import AdminTenantDetail from "@/pages/admin/AdminTenantDetail";
import TenantDashboard from "@/pages/tenant/TenantDashboard";
import TicketsPage from "@/pages/tenant/TicketsPage";
import TicketDetailPage from "@/pages/tenant/TicketDetailPage";
import TeamPage from "@/pages/tenant/TeamPage";
import IntegrationsPage from "@/pages/tenant/IntegrationsPage";
import StaffNewTicketPage from "@/pages/tenant/StaffNewTicketPage";
import AreasPage from "@/pages/tenant/AreasPage";
import HorariosPage from "@/pages/tenant/HorariosPage";
import NoticesPage from "@/pages/tenant/NoticesPage";
import ManifestationsPage from "@/pages/tenant/ManifestationsPage";
import PendenciasPage from "@/pages/tenant/PendenciasPage";
import ReservasPage from "@/pages/tenant/ReservasPage";
import ConvitesAcessoPage from "@/pages/tenant/ConvitesAcessoPage";
import AgendamentosPage from "@/pages/tenant/AgendamentosPage";
import FacialReportPage from "@/pages/tenant/FacialReportPage";
import PortariaTelefonePage from "@/pages/tenant/PortariaTelefonePage";
import ReservasPrintPage from "@/pages/tenant/ReservasPrintPage";
import MobileLoginPage from "@/pages/mobile/MobileLoginPage";
import ExcluirContaPage from "@/pages/ExcluirContaPage";
import ReservasReportPage from "@/pages/tenant/ReservasReportPage";
import AuditoriaPage from "@/pages/tenant/AuditoriaPage";
import ClientTicketsPage from "@/pages/client/ClientTicketsPage";
import NewTicketPage from "@/pages/client/NewTicketPage";
import ClientTicketDetailPage from "@/pages/client/ClientTicketDetailPage";
import ClientNoticesPage from "@/pages/client/ClientNoticesPage";
import { Toaster } from "@/components/ui/sonner";

function Guard({ roles, children }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Spinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function Home() {
  const { user, setUser } = useAuth();
  const [params] = useSearchParams();
  const [ssoError, setSsoError] = useState("");
  const ssoStarted = useRef(false);
  const itk = params.get("itk");
  const email = params.get("email");

  useEffect(() => {
    if (itk && email && !ssoStarted.current) {
      ssoStarted.current = true;
      api.post("/auth/token-login", { email, itk, name: params.get("client_name"), external_ref: params.get("external_ref") })
        .then(({ data }) => setUser(data))
        .catch((e) => setSsoError(errMsg(e)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (itk && email && !user) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-8">
        {ssoError ? (
          <div className="text-center max-w-sm" data-testid="sso-error">
            <p className="text-rose-400 bg-rose-400/10 text-sm rounded-md px-4 py-3 mb-4">{ssoError}</p>
            <Link to="/login" className="text-sm text-indigo-400 hover:text-indigo-300">Ir para o login manual</Link>
          </div>
        ) : (
          <div className="text-center" data-testid="sso-loading">
            <Spinner />
            <p className="text-sm text-zinc-400 mt-2">Conectando à sua organização…</p>
          </div>
        )}
      </div>
    );
  }

  if (user === undefined) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Spinner /></div>;
  const hasOrigin = itk && email && (params.get("client_name") || params.get("external_ref"));
  let target = homeFor(user);
  if (hasOrigin) {
    if (user.role === "client") target = "/portal/novo";
    else if (user.role !== "super_admin") target = "/app/novo";
  }
  return <Navigate to={target} replace />;
}

function App() {
  return (
    <div>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
            <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
            <Route path="/" element={<Home />} />
            <Route path="/app/reservas/print" element={<Guard roles={["tenant_admin", "agent"]}><ReservasPrintPage /></Guard>} />
            <Route path="/appmobile" element={<MobileLoginPage />} />
            <Route path="/excluir-conta" element={<ExcluirContaPage />} />

            <Route element={<Guard roles={["super_admin", "tenant_admin", "agent", "client"]}><Layout /></Guard>}>
              <Route path="/admin" element={<Guard roles={["super_admin"]}><AdminDashboard /></Guard>} />
              <Route path="/admin/tenants" element={<Guard roles={["super_admin"]}><AdminTenants /></Guard>} />
              <Route path="/admin/tenants/:id" element={<Guard roles={["super_admin"]}><AdminTenantDetail /></Guard>} />

              <Route path="/app" element={<Guard roles={["tenant_admin", "agent"]}><TenantDashboard /></Guard>} />
              <Route path="/app/chamados" element={<Guard roles={["tenant_admin", "agent"]}><TicketsPage /></Guard>} />
              <Route path="/app/novo" element={<Guard roles={["tenant_admin", "agent"]}><StaffNewTicketPage /></Guard>} />
              <Route path="/app/areas" element={<Guard roles={["tenant_admin", "agent"]}><AreasPage /></Guard>} />
              <Route path="/app/horarios" element={<Guard roles={["tenant_admin", "agent"]}><HorariosPage /></Guard>} />
              <Route path="/app/mural" element={<Guard roles={["tenant_admin", "agent"]}><NoticesPage /></Guard>} />
              <Route path="/app/manifestacoes" element={<Guard roles={["tenant_admin", "agent"]}><ManifestationsPage /></Guard>} />
              <Route path="/app/relatorios/pendencias" element={<Guard roles={["tenant_admin", "agent"]}><PendenciasPage /></Guard>} />
              <Route path="/app/reservas" element={<Guard roles={["tenant_admin", "agent"]}><ReservasPage /></Guard>} />
              <Route path="/app/convites-acesso" element={<Guard roles={["tenant_admin", "agent"]}><ConvitesAcessoPage /></Guard>} />
              <Route path="/app/agendamentos" element={<Guard roles={["tenant_admin", "agent"]}><AgendamentosPage /></Guard>} />
              <Route path="/recordweb" element={<Guard roles={["tenant_admin", "agent"]}><PortariaTelefonePage /></Guard>} />
              <Route path="/app/relatorios/reservas" element={<Guard roles={["tenant_admin", "agent"]}><ReservasReportPage /></Guard>} />
              <Route path="/app/relatorios/auditoria" element={<Guard roles={["tenant_admin", "agent"]}><AuditoriaPage /></Guard>} />
              <Route path="/app/relatorios/facial" element={<Guard roles={["tenant_admin", "agent"]}><FacialReportPage /></Guard>} />
              <Route path="/app/chamados/:id" element={<Guard roles={["tenant_admin", "agent"]}><TicketDetailPage /></Guard>} />
              <Route path="/app/equipe" element={<Guard roles={["tenant_admin", "agent"]}><TeamPage /></Guard>} />
              <Route path="/app/integracoes" element={<Guard roles={["tenant_admin"]}><IntegrationsPage /></Guard>} />

              <Route path="/portal" element={<Guard roles={["client"]}><ClientTicketsPage /></Guard>} />
              <Route path="/portal/novo" element={<Guard roles={["client"]}><NewTicketPage /></Guard>} />
              <Route path="/portal/mural" element={<Guard roles={["client"]}><ClientNoticesPage /></Guard>} />
              <Route path="/portal/chamados/:id" element={<Guard roles={["client"]}><ClientTicketDetailPage /></Guard>} />
            </Route>

            <Route path="*" element={<Home />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="top-right" closeButton />
      </AuthProvider>
    </div>
  );
}

export default App;
